<template>
  <div class="global-search-menu-topic">
    <!-- empty data -->
    <div
      v-if="!searchKey || menuData.every((item) => !item.data)"
      class="tw-flex tw-flex-col tw-w-full tw-h-full tw-items-start tw-justify-start tw-p-[25px]"
    >
      <div class="global-search-menu-topic-empty-text">
        {{ getComponentUiText("app-header-menu-search-text-empty-1") }}
      </div>
      <ModalHoverSearchAssessment
        v-if="listResentSearch.length > 0"
        :is-search-modal-assessment="false"
        :search-type-id="SEARCH_TYPE_ID.GLOB"
        :custom-class="'tw-border-none tw-rounded-[7px] !tw-px-0'"
        @update-current-value-search="updateCurrentValueSearchKey"
      />
    </div>
    <div
      v-else-if="
        searchKey &&
        menuData.every(
          (item) => !item.loading && item.data && item.data.length === 0,
        )
      "
      class="tw-flex tw-w-full tw-h-full tw-items-center tw-justify-center tw-text-center tw-p-5"
    >
      <span class="global-search-menu-topic-empty-text">
        {{ getComponentUiText("app-header-menu-search-text-not-found-1") }}
      </span>
    </div>

    <!-- show data -->
    <template v-else>
      <div
        v-for="item in menuData"
        :key="item.id"
        :class="`global-search-menu-topic-item ${
          selectedFilterIds.length === 1 &&
          !selectedFilterIds.includes(TopicName.all)
            ? 'global-search-menu-topic-item-alone'
            : ''
        }`"
      >
        <div class="global-search-menu-topic-item-title">
          <span>{{ getComponentUiText(item.title) }}</span>
        </div>
        <!-- loading -->
        <div
          v-if="item.loading"
          class="global-search-menu-topic-item-content custom-global-skeleton"
        />
        <div v-else class="global-search-menu-topic-item-content">
          <div v-if="!item.data?.length" class="tw-flex tw-items-center">
            <span class="global-search-menu-topic-item-not-found-text">
              {{
                getComponentUiText("app-header-menu-search-text-not-found-1")
              }}
            </span>
          </div>
          <template v-else>
            <div v-for="(data, keyItem) in item.data" :key="keyItem">
              <PageTopic
                v-if="item.id === TopicName.page"
                :data="data"
                @close-menu="$emit('close-menu')"
              />
              <OrgTopic
                v-else-if="item.id === TopicName.org"
                :data="data"
                @close-menu="$emit('close-menu')"
              />
              <RecommendTopic
                v-else-if="item.id === TopicName.recommend"
                :data="data"
                @close-menu="$emit('close-menu')"
              />
              <template v-else-if="item.id === TopicName.help">help</template>
              <MemberTopic
                v-else-if="item.id === TopicName.member"
                :data="data"
                @close-menu="$emit('close-menu')"
              />
              <RoleTopic
                v-else-if="item.id === TopicName.role"
                :data="data"
                @close-menu="$emit('close-menu')"
              />
              <AssessmentTopic
                v-else
                :data="data"
                @close-menu="$emit('close-menu')"
              />
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { getComponentUiText, searchKeyValue } from "@/utils/utilities";
import { routers, SEARCH_TYPE_ID } from "@/utils/constants";
import store from "@/store";
import {
  getExternalMembersOrg,
  getInternalMembersOrg,
  getOrgRoles,
} from "@/services/org";
import {
  getAllChecklist,
  getDetailFromCheckListId,
} from "@/services/assessment";
import type { IAssessmentDetail } from "@/interfaces/assessments/IAssessmentDetails";
import { TopicName } from "@/components/common/globalSearch/constants";

/** components */
import PageTopic from "@/components/common/globalSearch/menuSearch/pageTopic.vue";
import OrgTopic from "@/components/common/globalSearch/menuSearch/orgTopic.vue";
import RecommendTopic from "@/components/common/globalSearch/menuSearch/recommendTopic.vue";
import MemberTopic from "@/components/common/globalSearch/menuSearch/memberTopic.vue";
import RoleTopic from "@/components/common/globalSearch/menuSearch/roleTopic.vue";
import AssessmentTopic from "@/components/common/globalSearch/menuSearch/assessmentTopic.vue";
import ModalHoverSearchAssessment from "@/components/assessment/modalHoverSearchAssessment.vue";
import { showErrorAPI } from "@/services/apiRoutes";
import { Org } from "@/services/apiRoutes/routes";

const props = defineProps<{
  selectedFilterIds: string[];
  searchKey: string;
  searching: number;
}>();

const emit = defineEmits<{
  (e: "on-select", id: string): void;
  (e: "close-menu"): void;
}>();

const listResentSearch = computed(() => {
  return store.getters["Assessment/getRecentSearch"](SEARCH_TYPE_ID.GLOB);
});

const updateCurrentValueSearchKey = (value: string) => {
  emit("on-select", value);
};

const dataQueryPage = [
  [
    {
      title: getComponentUiText("app-menu-home-1"),
      url: routers.dashboard,
      description: getComponentUiText(
        "app-menu-submenu-dashboard-page-description-1",
      ),
      descriptionInSearchModal: getComponentUiText(
        "app-header-menu-search-page-description-home-1",
      ),
    },
  ],
  [
    {
      title: getComponentUiText("app-menu-submenu-assess-manage-assessments-1"),
      url: routers.assessCreate,
      description: getComponentUiText(
        "app-menu-submenu-assessments-create-page-description-1",
      ),
      descriptionInSearchModal: getComponentUiText(
        "app-header-menu-search-page-description-mange-assessments-1",
      ),
    },
  ],
  [
    {
      title: getComponentUiText(
        "app-menu-submenu-assess-complete-assessments-1",
      ),
      url: routers.assessChecklist,
      description: getComponentUiText(
        "app-menu-submenu-assessments-input-page-description-1",
      ),
      descriptionInSearchModal: getComponentUiText(
        "app-header-menu-search-page-description-complete-ssessments-1",
      ),
    },
  ],
  [
    {
      title: getComponentUiText("app-menu-submenu-assess-recommendations-1"),
      url: routers.assessRecommend,
      description: getComponentUiText(
        "app-menu-submenu-recommendation-page-description-1",
      ),
      descriptionInSearchModal: getComponentUiText(
        "app-header-menu-search-page-description-recommendations-1",
      ),
    },
  ],
  [
    {
      title: getComponentUiText("app-menu-submenu-share-cyber-health-report-1"),
      url: routers.shareProfile,
      description: getComponentUiText(
        "app-menu-submenu-cyber-report-page-description-1",
      ),
      descriptionInSearchModal: getComponentUiText(
        "app-header-menu-search-page-description-cyber-health-report-1",
      ),
    },
  ],
  [
    {
      title: getComponentUiText("app-menu-submenu-assess-vendor-assessments-1"),
      url: routers.assessVendor,
      description: getComponentUiText(
        "app-menu-submenu-assessments-vendor-page-description-1",
      ),
      descriptionInSearchModal: getComponentUiText(
        "app-header-menu-search-page-description-vendor-assessments-1",
      ),
    },
  ],
  [
    {
      title: getComponentUiText("app-menu-submenu-scan-scanning-1"),
      url: routers.scanScanning,
      description: getComponentUiText(
        "app-menu-submenu-scanning-page-description-1",
      ),
      descriptionInSearchModal: getComponentUiText(
        "app-header-menu-search-page-description-scanning-1",
      ),
    },
  ],

  [
    {
      title: getComponentUiText("app-menu-submenu-settings-setup-1"),
      url: routers.settingsSetup,
      description: getComponentUiText(
        "app-header-menu-search-page-description-settings-setup-1",
      ),
      descriptionInSearchModal: getComponentUiText(
        "app-header-menu-search-page-description-settings-setup-1",
      ),
    },

    {
      children: [
        {
          title: `${getComponentUiText("app-menu-submenu-settings-1")} >
          ${getComponentUiText(
            "app-menu-submenu-settings-org-1",
          )} > ${getComponentUiText(
            "app-menu-submenu-settings-org-details-1",
          )}`,
          url: routers.settingOrgDetails,
          descriptionInSearchModal: getComponentUiText(
            "app-header-menu-search-page-description-settings-organisation-details-1",
          ),
        },
        {
          title: `${getComponentUiText("app-menu-submenu-settings-1")} >
          ${getComponentUiText(
            "app-menu-submenu-settings-org-1",
          )} > ${getComponentUiText(
            "app-menu-submenu-settings-org-profile-1",
          )}`,
          url: routers.settingOrgProfile,
          descriptionInSearchModal: getComponentUiText(
            "app-header-menu-search-page-description-settings-organisation-profile-1",
          ),
        },
        {
          title: `${getComponentUiText("app-menu-submenu-settings-1")} >
          ${getComponentUiText(
            "app-menu-submenu-settings-org-1",
          )} > ${getComponentUiText(
            "app-menu-submenu-settings-org-manage-access-1",
          )}`,
          url: routers.settingOrgManageAccess,
          descriptionInSearchModal: getComponentUiText(
            "app-header-menu-search-page-description-settings-organisation-manage-access-1",
          ),
        },
      ],
    },
    {
      title: `${getComponentUiText("app-menu-submenu-settings-1")} >
          ${getComponentUiText("app-menu-submenu-settings-domains-1")}`,
      url: routers.settingDomains,
      descriptionInSearchModal: getComponentUiText(
        "app-header-menu-search-page-description-settings-domains-1",
      ),
    },
    {
      children: [
        {
          title: `${getComponentUiText("app-menu-submenu-settings-1")} >
          ${getComponentUiText(
            "app-menu-submenu-settings-team-1",
          )} > ${getComponentUiText(
            "app-menu-submenu-settings-team-members-1",
          )}`,
          url: routers.settingTeamMembers,
          descriptionInSearchModal: getComponentUiText(
            "app-header-menu-search-page-description-settings-team-members-1",
          ),
        },
        {
          title: `${getComponentUiText("app-menu-submenu-settings-1")} >
          ${getComponentUiText(
            "app-menu-submenu-settings-team-1",
          )} > ${getComponentUiText("app-menu-submenu-settings-team-roles-1")}`,
          url: routers.settingTeamRoles,
          descriptionInSearchModal: getComponentUiText(
            "app-header-menu-search-page-description-settings-team-roles-1",
          ),
        },
      ],
    },
    {
      title: `${getComponentUiText("app-menu-submenu-settings-1")} >
          ${getComponentUiText("app-menu-submenu-settings-subscription-1")} `,
      description: getComponentUiText(
        "app-header-menu-search-page-description-settings-subscription-1",
      ),
      url: routers.settingSubscription,
      descriptionInSearchModal: getComponentUiText(
        "app-header-menu-search-page-description-settings-subscription-1",
      ),
    },
  ],
  [
    {
      title: `${getComponentUiText("app-menu-submenu-learn-help-1")} >
          ${getComponentUiText("app-menu-submenu-knowledge-base-1")} `,
      url: routers.learnHelp,
      descriptionInSearchModal: getComponentUiText(
        "app-header-menu-search-page-description-help-knowledge-base-1",
      ),
    },
  ],
];

const queryPage = async () => {
  // XCU-3301: Hide some temporary hidden pages in search result
  //  Should bring back when the pages are ready
  const pages = searchKeyValue(dataQueryPage, props.searchKey);

  cached.set(TopicName.page, pages);
  onUpdateData(TopicName.page, pages);
};

const queryOrg = async () => {
  const orgs = orgList.value.filter((org: any) =>
    isIncludesSearchKey(org.name),
  );
  cached.set(TopicName.org, orgs);
  onUpdateData(TopicName.org, orgs);
};

const queryRecommend = async () => {
  let recommends = store.state.Recommend.checklistGroupMetrics;
  if (!recommends.length) {
    const fetchChecklistData = await store.dispatch(
      "Recommend/fetchChecklistData",
    );
    if (!fetchChecklistData.latestChecklist) {
      onUpdateData(TopicName.recommend, []);
      return;
    }
    await store.dispatch("Recommend/fetchChecklistGroupMetrics");
    recommends = store.state.Recommend.checklistGroupMetrics;
  }
  recommends = recommends.filter((action: any) =>
    isIncludesSearchKey(action.checklistGroupName),
  );
  cached.set(TopicName.recommend, recommends);
  onUpdateData(TopicName.recommend, recommends);
};

const queryHelp = async () => {
  const helps = [];
  cached.set(TopicName.help, helps);
  onUpdateData(TopicName.help, helps);
};

const queryMember = async () => {
  const [internal, external] = await Promise.all([
    getInternalMembersOrg(),
    getExternalMembersOrg(),
  ]);
  const members = []
    .concat(internal?.data?.data || [])
    .concat(external?.data?.data || [])
    .filter((member: any) => {
      return (
        isIncludesSearchKey(member.firstName) ||
        isIncludesSearchKey(member.lastName)
      );
    });
  cached.set(TopicName.member, members);
  onUpdateData(TopicName.member, members);
};

const queryRole = async () => {
  try {
    const res = await getOrgRoles();
    const roles = (res?.data?.data || []).filter((role: any) =>
      isIncludesSearchKey(role.roleName),
    );
    cached.set(TopicName.role, roles);
    onUpdateData(TopicName.role, roles);
  } catch (error) {
    showErrorAPI(error, Org.getOrgRoles);
  }
};

const queryAssessment = async () => {
  try {
    const res = await getAllChecklist();
    const assessments = (res?.data?.data || []).filter(
      (assessment: IAssessmentDetail) =>
        isIncludesSearchKey(assessment.checklistName),
    );
    await Promise.all(
      assessments.map(async (item: any) => {
        const res = await getDetailFromCheckListId(item.checklistId);
        item.checklistStatus = res?.data?.data?.checklistStatus || "";
        item.checklistStatusName = res?.data?.data?.checklistStatusName || "";
        item.checklistStatusId = res?.data?.data?.checklistStatusId || "";
      }),
    );
    cached.set(TopicName.assessment, assessments);
    onUpdateData(TopicName.assessment, assessments);
  } catch (error) {
    console.error(error);
  }
};

const arrayTopics: {
  id: string;
  title: string;
  queryFn: () => Promise<void>;
}[] = [
  {
    id: TopicName.page,
    title: "app-header-menu-search-topic-page-1",
    queryFn: queryPage,
  },
  {
    id: TopicName.org,
    title: "app-header-menu-search-topic-org-1",
    queryFn: queryOrg,
  },
  {
    id: TopicName.recommend,
    title: "app-header-menu-search-topic-recommended-action-1",
    queryFn: queryRecommend,
  },
  {
    id: TopicName.help,
    title: "app-header-menu-search-topic-help-topic-1",
    queryFn: queryHelp,
  },
  {
    id: TopicName.member,
    title: "app-header-menu-search-topic-member-1",
    queryFn: queryMember,
  },
  {
    id: TopicName.role,
    title: "app-header-menu-search-topic-role-1",
    queryFn: queryRole,
  },
  {
    id: TopicName.assessment,
    title: "app-header-menu-search-topic-assessment-1",
    queryFn: queryAssessment,
  },
];

const cached = new Map();
// let timer: any;
const menuData = ref<
  {
    id: string;
    title: string;
    loading: boolean;
    data: any[] | null;
    queryFn: () => Promise<void>;
  }[]
>(
  arrayTopics.map((item) => {
    return {
      ...item,
      loading: false,
      data: null,
    };
  }),
);
const orgList = computed(() => store.state.Org.orgsList);

const convertVietnameseText = (text: string) => {
  return `${text || ""}`
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[đĐ]/g, "d")
    .toLowerCase();
};

const isIncludesSearchKey = (text: string) => {
  return convertVietnameseText(text.trim()).includes(
    convertVietnameseText(props.searchKey.trim()),
  );
};

const onUpdateData = (key: string, data: any[]) => {
  cached.set(key, data || []);
  menuData.value.some((item) => {
    if (item.id === key) {
      item.loading = false;
      item.data = data;
    }
  });
};

const onSearching = async () => {
  const queries = menuData.value.filter((item) => item.loading);
  await Promise.all(queries.map((item) => item.queryFn()));
};

onMounted(() => {
  arrayTopics.forEach((item) => {
    cached.set(item.id, []);
  });
});

watch(
  () => props.searchKey,
  () => {
    arrayTopics.forEach((item) => {
      cached.set(item.id, []);
    });
    menuData.value.forEach((item) => {
      item.data = null;
    });
  },
);

watch(
  () => props.searching,
  () => {
    if (props.searching) {
      menuData.value.forEach((item) => {
        item.loading = true;
      });
      onSearching();
      // clearTimeout(timer);
      // timer = setTimeout(onSearching, 300);
    }
  },
);

watch(
  () => props.selectedFilterIds,
  () => {
    const newTopics = props.selectedFilterIds.includes(TopicName.all)
      ? arrayTopics
      : arrayTopics.filter((item) => {
          if (props.selectedFilterIds.includes(item.id)) {
            return true;
          }
          if (props.selectedFilterIds.includes(TopicName.setting)) {
            return item.id === TopicName.member || item.id === TopicName.role;
          }
        });

    menuData.value = newTopics.map((item) => {
      const data = cached.get(item.id);
      return {
        ...item,
        loading: false,
        data,
      };
    });
    if (props.searchKey) {
      onSearching();
      // clearTimeout(timer);
      // timer = setTimeout(onSearching, 300);
    }
  },
);
</script>

<style lang="scss" scoped>
.global-search-menu-topic {
  width: 100%;
  max-height: 500px;
  overflow: auto;
  border: 1px solid #eff0f1;
  &::-webkit-scrollbar {
    width: 7px;
    height: 7px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 20px;
  }
  &:hover {
    &::-webkit-scrollbar-thumb {
      background-color: #eff0f1;
    }
  }
}
.global-search-menu-topic-empty-text {
  text-align: center;
  font-family: Poppins, sans-serif;
  font-size: 14px;
  font-weight: 400;
  user-select: none;
  color: #878787;
}
.global-search-menu-topic-item-not-found-text {
  font-family: Poppins, sans-serif;
  font-size: 11px;
  font-weight: 400;
  user-select: none;
  color: #878787;
}
.global-search-menu-topic-item {
  display: flex;
  padding: 18px 25px;
  border-bottom: 1px solid #eff0f1;
  min-width: 500px;
  min-height: 72px;
}
.global-search-menu-topic-item-alone {
  border-bottom: none;
}
.global-search-menu-topic-item-title {
  display: flex;
  align-items: center;
  width: 150px;
  min-width: 150px;
  & > span {
    height: 20px;
    border-radius: 4px;
    padding: 3px 5px;
    background-color: #eff0f1;
    font-family: Poppins, sans-serif;
    font-size: 11px;
    font-weight: 500;
    color: #b7b7b7;
    line-height: 15px;
  }
}
.global-search-menu-topic-item-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
}
</style>
